"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { en } from "@/i18n/en";
import { id, type Key } from "@/i18n/id";
import { ApiFail } from "./api";
import type { Lang } from "./format";

export type { Lang };

const dictionaries: Record<Lang, Record<Key, string>> = { id, en };

export type T = (key: Key, params?: Record<string, string | number>) => string;

type Ctx = { lang: Lang; setLang: (l: Lang) => void; t: T };
const I18nContext = createContext<Ctx>({ lang: "id", setLang: () => {}, t: (k) => k });

export function translate(lang: Lang, key: Key, params?: Record<string, string | number>): string {
  const text = dictionaries[lang][key] ?? id[key] ?? key;
  return params ? text.replace(/\{(\w+)\}/g, (_, name: string) => String(params[name] ?? `{${name}}`)) : text;
}

export function I18nProvider({ initial, children }: { initial: Lang; children: React.ReactNode }) {
  const [lang, setLangState] = useState<Lang>(initial);

  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    document.cookie = `lang=${l}; path=/; max-age=31536000; samesite=lax`;
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const value = useMemo<Ctx>(
    () => ({ lang, setLang, t: (key, params) => translate(lang, key, params) }),
    [lang, setLang],
  );
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export const useI18n = () => useContext(I18nContext);
export const useT = () => useContext(I18nContext).t;

/** Human text for an API error: the translated message for its code, else the server's English text. */
export function errorText(t: T, e: unknown): string {
  if (!(e instanceof ApiFail)) return t("err.unknown");
  const key = `err.${e.code}` as Key;
  if (key in id) {
    const n = e.extra.min_length ?? e.extra.length ?? e.extra.size_m2;
    return t(key, { n: typeof n === "number" ? n : "" });
  }
  return e.message || t("err.unknown");
}
